/**
 * A page as the core keeps it (`Page`: blocks with ids) and as the editor holds it (a
 * ProseMirror document), both ways. A block the editor does not know (a newer app's) becomes
 * a paragraph of its words rather than breaking the page. Every top-level block keeps its id;
 * one without an id, or with one taken by a block before it (a paragraph split in two copies
 * its id), gets a new one ({@link withIds}).
 */
import type { Node as PMNode } from "prosemirror-model";
import type { Block } from "@protocol/Block";
import type { Page } from "@protocol/Page";
import { docSchema } from "./schema";

/** A new block id: `b` and eight random letters or digits. */
export function freshId(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return `b${[...bytes].map((b) => b.toString(36).padStart(2, "0")).join("").slice(0, 8)}`;
}

/** The words of an editor node written as JSON (text, mentions' names), for a block nobody reads any more. */
function wordsOf(json: unknown): string {
  if (!json || typeof json !== "object") return "";
  const node = json as { type?: string; text?: string; attrs?: { name?: string }; content?: unknown[] };
  if (node.type === "text") return node.text ?? "";
  if (node.type === "mention") return node.attrs?.name ?? "";
  return (node.content ?? []).map(wordsOf).join(" ");
}

/** A block as the editor's node JSON (its id among its attributes). */
function nodeJson(block: Block): Record<string, unknown> {
  const json: Record<string, unknown> = { type: block.type, attrs: { ...block.attrs, id: block.id } };
  if (block.content.length) json.content = block.content;
  return json;
}

/** The editor's document for `page`. */
export function docOf(page: Pick<Page, "blocks">): PMNode {
  const content: PMNode[] = [];
  for (const block of page.blocks) {
    try {
      const node = docSchema.nodeFromJSON(nodeJson(block));
      node.check();
      content.push(node);
    } catch {
      const words = wordsOf({ content: block.content }).trim();
      content.push(docSchema.nodes.paragraph!.create({ id: block.id }, words ? docSchema.text(words) : null));
    }
  }
  if (!content.length) content.push(docSchema.nodes.paragraph!.create({ id: freshId() }));
  return docSchema.nodes.doc!.create(null, content);
}

/** The attributes worth keeping in a block: none at its default (no id, no conflict, left, not indented, a picture in line and centred). */
function keptAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (key === "id" || value === null || value === undefined || value === "") continue;
    if (key === "indent" && value === 0) continue;
    if (key === "order" && value === 1) continue;
    if (key === "width" && value === 100) continue;
    if ((key === "x" || key === "y") && value === 0) continue;
    if (key === "wrap" && value === "inline") continue;
    if (key === "align" && value === "center" && attrs.wrap !== undefined) continue;
    out[key] = value;
  }
  return out;
}

/** The blocks of the editor's document `doc`, each with its id (given one when it has none or a taken one). */
export function blocksOf(doc: PMNode): Block[] {
  const seen = new Set<string>();
  const blocks: Block[] = [];
  doc.forEach((node) => {
    const json = node.toJSON() as { type: string; attrs?: Record<string, unknown>; content?: unknown[] };
    let id = typeof node.attrs.id === "string" && node.attrs.id ? node.attrs.id : "";
    if (!id || seen.has(id)) id = freshId();
    seen.add(id);
    blocks.push({ id, type: json.type, attrs: keptAttrs(json.attrs ?? {}), content: json.content ?? [] });
  });
  return blocks;
}

/** Where the top-level blocks of `doc` need an id of their own (none, or one already taken above), by position. */
export function missingIds(doc: PMNode): number[] {
  const seen = new Set<string>();
  const out: number[] = [];
  doc.forEach((node, offset) => {
    const id = typeof node.attrs.id === "string" ? node.attrs.id : "";
    if (!id || seen.has(id)) out.push(offset);
    else seen.add(id);
  });
  return out;
}

/** Whether two pages say the same (blocks, parameters, layout and template), whatever the order of their keys. */
export function samePage(a: Page, b: Page): boolean {
  return canonical([a.template ?? null, sorted(a.params), a.layout, a.blocks]) === canonical([b.template ?? null, sorted(b.params), b.layout, b.blocks]);
}

/** `value` as JSON with every object's keys in order (the core sorts them, the editor does not). */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`)
      .join(",")}}`;
  return JSON.stringify(value ?? null);
}

/** `params` with its keys in order, for comparing. */
function sorted(params: Page["params"]): [string, string][] {
  return Object.entries(params)
    .filter((e): e is [string, string] => typeof e[1] === "string" && e[1] !== "")
    .sort(([a], [b]) => a.localeCompare(b));
}

/** The words of a page (titles, paragraphs, cells...), one block per paragraph. */
export function textOf(doc: PMNode): string {
  const parts: string[] = [];
  doc.forEach((node) => parts.push(node.textContent));
  return parts.filter((p) => p.trim()).join("\n\n");
}

/** How many words `text` has. */
export function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}
